/**
 * SYR-107 — every ending that is not the read-out.
 *
 * 'review' covers a queued request AND a blocked one, deliberately: the copy is
 * identical so the gate cannot be probed from outside. Someone who typed a
 * personal address and someone who typed a competitor's domain see the same
 * thing, and only the queue knows the difference.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';

export type NoticeKind =
    | 'review'
    | 'delayed'
    | 'load_failed'
    | 'insufficient_data'
    | 'failed'
    | 'link_expired'
    | 'unavailable'
    | 'unsubscribed';

const COPY: Record<NoticeKind, { titleId: string; title: string; bodyId: string; body: string }> = {
    review: {
        titleId: 'publicHypothesis.notice.reviewTitle',
        title: 'Thanks, we have this',
        bodyId: 'publicHypothesis.notice.reviewBody',
        body: "We look at some of these by hand before running them. If everything checks out you'll hear from Steve (steve@brandscience.ai).",
    },
    insufficient_data: {
        titleId: 'publicHypothesis.notice.thinTitle',
        title: 'Not enough to go on yet',
        bodyId: 'publicHypothesis.notice.thinBody',
        body: "We could not find enough public material about your brand to say anything worth reading. That is usually a good sign about your privacy and a bad sign about our method. Steve (steve@brandscience.ai) will be in touch, because there is another way to do this.",
    },
    // Every kind of upstream failure lands here, deliberately vaguely. Which
    // service was unavailable is our business, not the visitor's, and naming
    // it would either alarm them or advertise a billing problem. The promise
    // is kept either way: the lead sits in the review queue and finishing it
    // emails the result.
    delayed: {
        titleId: 'publicHypothesis.notice.delayedTitle',
        title: 'This is taking longer than it should',
        bodyId: 'publicHypothesis.notice.delayedBody',
        body: "Nothing you need to do. We'll email you the result as soon as it's ready.",
    },
    // A transient fetch failure on a read-out that DOES exist. Distinct from
    // 'delayed' (the run itself is stuck) and 'link_expired' (the token is
    // dead), because the fix here is simply to try again.
    load_failed: {
        titleId: 'publicHypothesis.notice.loadFailedTitle',
        title: "We couldn't load that just now",
        bodyId: 'publicHypothesis.notice.loadFailedBody',
        body: 'Refresh the page and it should come straight up. If it keeps happening, reply to the email and Steve (steve@brandscience.ai) will sort it out.',
    },
    failed: {
        titleId: 'publicHypothesis.notice.failedTitle',
        title: 'That did not finish',
        bodyId: 'publicHypothesis.notice.failedBody',
        body: 'Something went wrong on our side, not yours. Steve (steve@brandscience.ai) has been told and will pick it up from here.',
    },
    link_expired: {
        titleId: 'publicHypothesis.notice.expiredTitle',
        title: 'This link has expired',
        bodyId: 'publicHypothesis.notice.expiredBody',
        body: 'Read-out links do not last forever. Ask for a fresh one and we will send it straight over.',
    },
    unavailable: {
        titleId: 'publicHypothesis.notice.unavailableTitle',
        title: 'Temporarily unavailable',
        bodyId: 'publicHypothesis.notice.unavailableBody',
        body: 'The Hypothesis is switched off for the moment. Please try again later.',
    },
    unsubscribed: {
        titleId: 'publicHypothesis.notice.unsubscribedTitle',
        title: "You're unsubscribed",
        bodyId: 'publicHypothesis.notice.unsubscribedBody',
        body: 'We will not email you about BrandScience.ai again.',
    },
};

interface Props {
    kind: NoticeKind;
    // Server-held copy wins when present, so the disabled message in
    // particular is Steve's to change without a deploy.
    overrideBody?: string;
}

export const NoticeState: React.FC<Props> = ({ kind, overrideBody }) => {
    const copy = COPY[kind];
    return (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: THEME.textBase, margin: '0 0 12px' }}>
                <FormattedMessage id={copy.titleId} defaultMessage={copy.title} />
            </h2>
            <p
                style={{
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: '#334155',
                    maxWidth: 460,
                    margin: '0 auto',
                }}
            >
                {overrideBody ? (
                    overrideBody
                ) : (
                    <FormattedMessage id={copy.bodyId} defaultMessage={copy.body} />
                )}
            </p>
        </div>
    );
};
